import * as angular from "angular";
import {IPromise} from "angular";
import * as _ from "lodash";
import {BatchRequestsService} from "./batch-requests.service";
import {PollingIntervalService} from "../../commons/services/polling-interval.service";
import {BatchAlertsService} from "./batch-alerts.service";

interface BatchImportProgressOptions {
  template: string;
  getProgress: (batchId: string) => IPromise<any>;
  errorLog: string;
  entityLabel?: string;
  // Rows the caller sent. Only needed when the server's batch counts tasks instead of rows.
  totalRows?: number;
  // Builds the failed tasks list when the progress carries no `tasks` (v2 batch details).
  getErrorTasks?: (batch) => any[];
}

export class BatchDialogService {

  private PULLING_INTERVAL: number = 5000;

  /* @ngInject */
  constructor(private $mdDialog, private pollingIntervalService: PollingIntervalService, private batchRequestsService: BatchRequestsService,
              private batchAlertsService: BatchAlertsService, private $log) {
  }

  /**
   * CON-13947 - progress for the account agent assignment importers. `importerPath` selects
   * the importer (restricted / dedicated / preferred), `entityLabel` is the translated noun
   * shown in the totals row.
   */
  batchAgentAssignmentsProgress(event, batch, importerPath: string, entityLabel: string) {
    return this._batchImportProgress(event, batch, {
      template: require('./../views/dialogs/create-agent-assignments-batch-dialog.html').default,
      getProgress: (batchId: string) => this.batchRequestsService.getAgentAssignmentsBatchProgress(importerPath, batchId),
      errorLog: 'Failed to get agent assignments batch progress',
      entityLabel: entityLabel
    });
  }

  /**
   * PRD-1726 - progress for the price plan (program section) importers. The server packs up to
   * 100 rows into one batch task, so `batch.details` counts tasks and not rows: the dialog shows
   * the `totalRows` the caller sent plus the batch status instead of the task counters.
   */
  batchProgramImportProgress(event, batch, statusPath: string, entityLabel: string, totalRows: number) {
    return this._batchImportProgress(event, batch, {
      template: require('./../views/dialogs/create-program-import-batch-dialog.html').default,
      getProgress: (batchId: string) => this.batchRequestsService.getAgentAssignmentsBatchProgress(statusPath, batchId),
      errorLog: 'Failed to get program import batch progress',
      entityLabel: entityLabel,
      totalRows: totalRows
    });
  }

  /**
   * Progress for importers whose /status returns the v2 batch details `{batch, totals}` (tasks
   * counted by status, no task list). The batch is unwrapped so the poller keeps reading `id` and
   * `details.status`; failed and timed out tasks are reported as error tasks without row numbers,
   * since one task holds a chunk of rows.
   */
  batchV2ImportProgress(event, batch, statusPath: string, entityLabel: string, totalRows: number) {
    return this._batchImportProgress(event, batch, {
      template: require('./../views/dialogs/create-program-import-batch-dialog.html').default,
      getProgress: (batchId: string) => this.batchRequestsService.getAgentAssignmentsBatchProgress(statusPath, batchId)
        .then((progress: any) => ({...progress.batch, totals: progress.totals})),
      errorLog: 'Failed to get import batch progress',
      entityLabel: entityLabel,
      totalRows: totalRows,
      getErrorTasks: (progressBatch) => {
        const failedTasks = _.get(progressBatch, 'totals.failed', 0) + _.get(progressBatch, 'totals.timeOut', 0);
        return _.times(failedTasks, () => ({rowNumber: -1, mappingErrors: []}));
      }
    });
  }

  batchOmsProgress(event, batch) {
    return this._batchImportProgress(event, batch, {
      template: require('./../views/dialogs/create-oms-batch-dialog.html').default,
      getProgress: (batchId: string) => this.batchRequestsService.getOmsBatchProgress(batchId),
      errorLog: 'Failed to get batch progress'
    });
  }

  private _batchImportProgress(event, batch, options: BatchImportProgressOptions) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: options.template,
      controller: function ($scope) {
        $scope.batch = batch;
        $scope.loading = true;
        $scope.entityLabel = options.entityLabel;
        $scope.totalRows = options.totalRows;
        $scope.errorTasks = undefined;
        $scope.progressError = false;
        $scope.serverErrors = [];

        $scope.onDone = () => {
          self.$mdDialog.hide([]);
        };

        $scope.onFix = () => {
          self.$mdDialog.hide($scope.errorTasks);
        };

        $scope.onClose = () => {
          self.$mdDialog.cancel();
        };

        let poller;
        const getProgress = (hidePayload: boolean) => {
          options.getProgress($scope.batch.id)
            .then((batch) => {
              $scope.batch = batch;
              if (!hidePayload) {
                $scope.loading = false;
                $scope.errorTasks = options.getErrorTasks ? options.getErrorTasks(batch) : _.chain(batch.tasks || [])
                  .filter((t) => {
                    return t.status !== 'Completed';
                  })
                  .map((filteredTask) => {
                    const rowNumber = _.get(filteredTask, 'metadataMap.rowNumber', -1);
                    return {
                      rowNumber: parseInt(rowNumber),
                      mappingErrors: filteredTask.errorDescription ? [filteredTask.errorDescription] : []
                    };
                  })
                  .value();
              }
            })
            .catch((error) => {
              poller.cancel();
              $scope.loading = false;
              $scope.progressError = true;
              self.batchAlertsService.importEntitiesError(error);
              self.$log.error(options.errorLog, error);
            });
        };

        poller = self.pollingIntervalService.start($scope, () => {
          const status = _.get($scope.batch, 'details.status');
          if (status !== 'Completed' && status !== 'CompletedWithErrors') {
            getProgress(true);
          } else {
            getProgress(false);
            poller.cancel();
          }
        }, self.PULLING_INTERVAL);

      },
      targetEvent: event,
      clickOutsideToClose: false
    });
  }

  batchServicesProgress(event, batch) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/create-batch-dialog.html').default,
      controller: function ($scope) {

        const poller = self.pollingIntervalService.start($scope, () => {
          const status = _.get($scope.batch, 'details.status');
          if (status !== 'Completed' && status !== 'CompletedWithErrors') {
            $scope._getProgress($scope.batch, true);
          } else {
            $scope._getProgress($scope.batch, false);
            poller.cancel();
          }
        }, self.PULLING_INTERVAL);

        $scope.batch = batch;
        $scope.loading = true;

        $scope.errorTasks = undefined;
        $scope.serverErrors = [];

        // ******************************
        //        Public methods
        // ******************************

        $scope.onDone = () => {
          self.$mdDialog.hide([]);
        }

        $scope.onFix = () => {
          self.$mdDialog.hide($scope.errorTasks);
        }

        // ******************************
        //        Private methods
        // ******************************

        $scope._getProgress = (batch, hidePayload) => {
          self.batchRequestsService.getServicesBatchProgress(batch.id)
            .then((batch) => {
              $scope.batch = batch;
              if (!hidePayload) {
                $scope.loading = false;
                $scope.errorTasks = _.chain($scope.batch.tasks)
                  .filter((t) => {
                    return t.status !== 'Completed';
                  })
                  .map((filteredTask) => {
                    const rowNumber = _.get(filteredTask, 'metadataMap.rowNumber', -1);
                    return {
                      rowNumber: parseInt(rowNumber),
                      mappingErrors: filteredTask.errorDescription ? [filteredTask.errorDescription] : []
                    };
                  })
                  .value();
              }
            })
            .catch((error) => {
              self.$log.error('Failed to get batch progress', error);
            })
        }

      },
      targetEvent: event,
      clickOutsideToClose: false
    })
  }

  batchRecurringServicesProgress(event, batch) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/create-batch-dialog.html').default,
      controller: function ($scope) {

        const poller = self.pollingIntervalService.start($scope, () => {
          const status = _.get($scope.batch, 'details.status');
          if (status !== 'Completed' && status !== 'CompletedWithErrors') {
            $scope._getProgress($scope.batch, true);
          } else {
            $scope._getProgress($scope.batch, false);
            poller.cancel();
          }
        }, self.PULLING_INTERVAL);

        $scope.batch = batch;
        $scope.loading = true;

        $scope.errorTasks = undefined;
        $scope.serverErrors = [];

        // ******************************
        //        Public methods
        // ******************************

        $scope.onDone = () => {
          self.$mdDialog.hide([]);
        }

        $scope.onFix = () => {
          self.$mdDialog.hide($scope.errorTasks);
        }

        // ******************************
        //        Private methods
        // ******************************

        $scope._getProgress = (batch, hidePayload) => {
          self.batchRequestsService.getRecurringServicesBatchProgress(batch.id, hidePayload)
            .then((batch) => {
              $scope.batch = batch;

              if (!hidePayload) {
                $scope.loading = false;
                $scope.errorTasks = _.chain($scope.batch.tasks)
                  .filter((t) => {
                    return t.status !== 'Completed';
                  })
                  .map((filteredTask) => {
                    const rowNumber = _.get(filteredTask, 'metadataMap.rowNumber', -1);
                    return {
                      rowNumber: parseInt(rowNumber),
                      mappingErrors: filteredTask.errorDescription ? [filteredTask.errorDescription] : []
                    };
                  })
                  .value();
              }
            })
            .catch((error) => {
              self.$log.error('Failed to get batch progress', error);
            })
        }

      },
      targetEvent: event,
      clickOutsideToClose: false
    })
  }

  batchAccountsProgress(event, batch) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/create-accounts-batch-dialog.html').default,
      controller: function ($scope) {

        const poller = self.pollingIntervalService.start($scope, () => {
          const status = _.get($scope.batch, 'details.status');
          if (status !== 'Completed' && status !== 'CompletedWithErrors') {
            $scope._getProgress($scope.batch, true);
          } else {
            $scope._getProgress($scope.batch, false);
            poller.cancel();
          }
        }, self.PULLING_INTERVAL);

        $scope.batch = batch;
        $scope.loading = true;

        $scope.errorTasks = undefined;
        $scope.serverErrors = [];

        // ******************************
        //        Public methods
        // ******************************

        $scope.onDone = () => {
          self.$mdDialog.hide([]);
        }

        $scope.onFix = () => {
          self.$mdDialog.hide($scope.errorTasks);
        }

        // ******************************
        //        Private methods
        // ******************************

        $scope._getProgress = (batch, hidePayload) => {
          self.batchRequestsService.getServicesBatchProgress(batch.id)
            .then((batch) => {
              $scope.batch = batch;
              if (!hidePayload) {
                $scope.loading = false;
                $scope.errorTasks = _.chain($scope.batch.tasks)
                  .filter((t) => {
                    return t.status !== 'Completed';
                  })
                  .map((filteredTask) => {
                    const rowNumber = _.get(filteredTask, 'metadataMap.rowNumber', -1);
                    return {
                      rowNumber: parseInt(rowNumber),
                      mappingErrors: filteredTask.errorDescription ? [filteredTask.errorDescription] : []
                    };
                  })
                  .value();
              }
            })
            .catch((error) => {
              self.$log.error('Failed to get batch progress', error);
            })
        }

      },
      targetEvent: event,
      clickOutsideToClose: false
    })
  }

  batchAgentsProgress(event, batch) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/create-agents-batch-dialog.html').default,
      controller: function ($scope) {

        const poller = self.pollingIntervalService.start($scope, () => {
          const status = _.get($scope.batch, 'details.status');
          if (status !== 'Completed' && status !== 'CompletedWithErrors') {
            $scope._getProgress($scope.batch, true);
          } else {
            $scope._getProgress($scope.batch, false);
            poller.cancel();
          }
        }, self.PULLING_INTERVAL);

        $scope.batch = batch;
        $scope.loading = true;

        $scope.errorTasks = undefined;
        $scope.serverErrors = [];

        // ******************************
        //        Public methods
        // ******************************

        $scope.onDone = () => {
          self.$mdDialog.hide([]);
        }

        $scope.onFix = () => {
          self.$mdDialog.hide($scope.errorTasks);
        }

        // ******************************
        //        Private methods
        // ******************************

        $scope._getProgress = (batch, hidePayload) => {
          self.batchRequestsService.getAgentsBatchProgress(batch.id)
            .then((batch) => {
              $scope.batch = batch;
              if (!hidePayload) {
                $scope.loading = false;
                $scope.errorTasks = _.chain($scope.batch.tasks)
                  .filter((t) => {
                    return t.status !== 'Completed';
                  })
                  .map((filteredTask) => {
                    const rowNumber = _.get(filteredTask, 'metadataMap.rowNumber', -1);
                    return {
                      rowNumber: parseInt(rowNumber),
                      mappingErrors: filteredTask.errorDescription ? [filteredTask.errorDescription] : []
                    };
                  })
                  .value();
              }
            })
            .catch((error) => {
              self.$log.error('Failed to get batch progress', error);
            })
        }

      },
      targetEvent: event,
      clickOutsideToClose: false
    })
  }

}

angular
  .module('bringoz.admin.batch')
  .service('batchDialogService', BatchDialogService);
